import * as liveEventModel from 'models/liveEvent';
import {
    AudioChannelsMixType,
    AudioChannelsMixValuesStereo,
    AudioChannelsMixValuesDualMono,
    AudioChannelsMixValuesIn3n4,
} from 'components/video/live/form/AudioConfiguration/AudioConfiguration';

/**
 * @typedef {LiveEvent[]} MultiLiveEvent
 */

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function noEvents(events = []) {
    return events.length === 0;
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function atLeastOneEvent(events = []) {
    return events.length > 0;
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function isMultiEvent(events = []) {
    return events.length > 1;
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function allEventsAreRunning(events = []) {
    return atLeastOneEvent(events) && events.every(liveEventModel.isRunning);
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function isMultiEventRunning(events = []) {
    return (
        atLeastOneEvent(events) &&
        (events.every(liveEventModel.isRunning) ||
            (events.some(liveEventModel.isRunning) && events.some(liveEventModel.hasError)))
    );
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function isMultiEventPending(events = []) {
    return (
        atLeastOneEvent(events) &&
        (events.every(liveEventModel.isPending) ||
            (events.some(liveEventModel.isPending) && events.some(liveEventModel.hasError)))
    );
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function atLeastOneEventIsRunning(events = []) {
    return events.some(liveEventModel.isRunning);
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function allEventsHaveError(events = []) {
    return atLeastOneEvent(events) && events.every(liveEventModel.hasError);
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function allEventsArePending(events = []) {
    return atLeastOneEvent(events) && events.every(liveEventModel.isPending);
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function atLeastOneEventIsPending(events = []) {
    return !atLeastOneEventIsRunning(events) && events.some(liveEventModel.isPending);
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Number|null}
 */
export function getStartTime(events = []) {
    return (
        events
            .map(liveEventModel.getStartTime)
            .filter(Boolean)
            .sort((a, b) => a - b)
            .shift() || null
    );
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Number|null}
 */
export function getEndTime(events = []) {
    return events
        .map(liveEventModel.getEndTime)
        .filter(Boolean)
        .sort((a, b) => a - b)
        .shift();
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Number|null}
 */
export function getDvrLength(events = []) {
    return events
        .map(({ dvrLength }) => dvrLength)
        .filter(Boolean)
        .shift();
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean|null}
 */
export function getWscRtmpOutput(events = []) {
    return events
        .map(({ hasWscRtmpOutput }) => hasWscRtmpOutput)
        .filter(Boolean)
        .shift();
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Object|null}
 */
export function getAudioChannelsMix(events = []) {
    return events
        .map(({ audioChannelsMix }) => audioChannelsMix)
        .filter(Boolean)
        .shift();
}

const AUDIO_MIX_PRESETS = [
    { label: 'Default stereo', type: AudioChannelsMixType.STEREO, values: AudioChannelsMixValuesStereo },
    { label: 'Dual mono', type: AudioChannelsMixType.DUALMONO, values: AudioChannelsMixValuesDualMono },
    { label: '3+4 stereo', type: AudioChannelsMixType.IN3N4, values: AudioChannelsMixValuesIn3n4 },
];

function channelsToBooleans(channels) {
    const result = Array(5).fill(false);

    for (const { channelId, gain } of channels) {
        result[channelId] = gain === 0;
    }

    return result;
}

function findAudioMixPreset(audioChannelsMix) {
    if (!audioChannelsMix?.stereoMix) {
        return { preset: AUDIO_MIX_PRESETS[0] };
    }
    const left = channelsToBooleans(audioChannelsMix.stereoMix.leftChannel);
    const right = channelsToBooleans(audioChannelsMix.stereoMix.rightChannel);
    const preset = AUDIO_MIX_PRESETS.find(
        (p) =>
            p.values.leftChannel.every((v, i) => v === left[i]) && p.values.rightChannel.every((v, i) => v === right[i])
    );
    return { preset, left, right };
}

/**
 * @param {Object|null|undefined} audioChannelsMix
 * @returns {string}
 */
export function getAudioChannelsMixLabel(audioChannelsMix) {
    const { preset } = findAudioMixPreset(audioChannelsMix);
    return preset ? preset.label : 'Custom mixing';
}

/**
 * @param {Object|null|undefined} audioChannelsMix
 * @returns {{ type: string, values: { leftChannel: boolean[], rightChannel: boolean[] } }}
 */
export function getAudioChannelsMixFormValues(audioChannelsMix) {
    const { preset, left, right } = findAudioMixPreset(audioChannelsMix);
    if (preset) {
        return { type: preset.type, values: preset.values };
    }
    return { type: AudioChannelsMixType.CUSTOM, values: { leftChannel: left, rightChannel: right } };
}

/**
 * @param {MultiLiveEvent} [events=[]]
 * @returns {Boolean}
 */
export function isMultiLiveEventIdle(events = []) {
    const startTime = getStartTime(events);
    return allEventsArePending(events) && (!startTime || startTime < Date.now() / 1000);
}
